import React from 'react';
import Rating from '../../components/banks/Rating';
import { StaticImage } from 'gatsby-plugin-image';
import { Link, useTranslation } from 'gatsby-plugin-react-i18next';
import cx from 'classnames';
import dayjs from 'dayjs';
require('dayjs/locale/nl');
import { BanksDataType } from './banksData';

// Assets
import success from '../../assets/images/banks/details/success.svg';
import warning from '../../assets/images/banks/details/warning.svg';
import applePay from '../../assets/images/banks/details/apple-pay.svg';
import googlePay from '../../assets/images/banks/details/google-pay.svg';
import okApp from '../../assets/images/banks/details/ok-app.svg';
import spending from '../../assets/images/banks/details/spending.svg';
import savings from '../../assets/images/banks/details/savings.svg';
import flow from '../../assets/images/banks/details/flow.svg';
import daan from '../../assets/images/banks/details/daan-img.png';
import niels from '../../assets/images/banks/details/niels-img.png';

interface ListItemProps {
  children: React.ReactNode;
  className?: string;
  type: 'success' | 'warning';
}

const ListItem = ({ children, className, type }: ListItemProps) => {
  return (
    <li className={className}>
      <img src={type === 'success' ? success : warning} width="20px" alt="o" />
      <span>{children}</span>
    </li>
  );
};

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

interface BankTemplateProps {
  data: BanksDataType;
  type: 'personal' | 'business';
}

const BankTemplate = ({ data, type }: BankTemplateProps) => {
  const { t, i18n } = useTranslation('bankDetails');
  return (
    <main className="bank-details-page">
      <section className="section-1">
        <div className="container">
          <div className="section-1__content">
            <div className="section-1__row">
              <div className="section-1__col">
                <img src={data.logo} alt="logo" className="section-1__img" />
              </div>
              <div className="section-1__col">
                <div className="section-1__option">
                  {t(`heroSection.type.${type}`)}
                </div>
                <div className="section-1__wrapper">
                  <h2 className="section-1__title">{data.name}</h2>
                  <div
                    className={cx('section-1__status', {
                      'section-1__status--available':
                        data.status === 'available',
                      'section-1__status--planned': data.status === 'planned',
                      'section-1__status--coming': data.status === 'coming',
                    })}
                  >
                    {t(`heroSection.statuses.${data.status}`)}
                  </div>
                </div>
              </div>
            </div>
            <h3 className="section-1__subtitle">
              {t('heroSection.updatedAt', {
                date: dayjs(data.updatedAt)
                  .locale(i18n.language)
                  .format('MMMM D, YYYY'),
              })}
            </h3>
            <p className="section-1__description">
              {t('heroSection.description')}
            </p>
            <article className="section-1__card">
              <Rating level={data.flowScore} large />
              <div className="section-1__price">
                <div className="section-1__amount">
                  {t('priceSection.amount', {
                    amount: `${data.pricePerMonth[type]}`,
                  })}
                </div>
                <div className="section-1__remark">
                  <strong>{t('priceSection.plan_1')}</strong>
                  <span>{t('priceSection.plan_2')}</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
      <section className="section-2">
        <div className="container">
          <div className="section-2__content">
            <div className="section-2__block">
              <h3 className="section-2__title">
                {t('benefintsSection.pros.title')}
              </h3>
              <ul className="section-2__list">
                {data.pros.map((el, idx) => (
                  <ListItem
                    className="section-2__item"
                    key={idx}
                    type="success"
                  >
                    {t(`benefintsSection.pros.${el}`)}
                  </ListItem>
                ))}
              </ul>
            </div>
            <div className="section-2__block">
              <h3 className="section-2__title">
                {t('benefintsSection.cons.title')}
              </h3>
              <ul className="section-2__list">
                {data.cons.map((el, idx) => (
                  <ListItem
                    className="section-2__item"
                    key={idx}
                    type="warning"
                  >
                    {t(`benefintsSection.cons.${el}`)}
                  </ListItem>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      <section className="section-3">
        <div className="container">
          <div className="section-3__card">
            <div className="section-3__col">
              <img src={applePay} alt="" className="section-3__img" />
              <h3 className="section-3__title">{t('optionsSection.0')}</h3>
            </div>
            <div className="section-3__col">
              <img src={googlePay} alt="" className="section-3__img" />
              <h3 className="section-3__title">{t('optionsSection.1')}</h3>
            </div>
            <div className="section-3__col">
              <img src={okApp} alt="" className="section-3__img" />
              <h3 className="section-3__title">{t('optionsSection.2')}</h3>
            </div>
          </div>
        </div>
      </section>
      <section className="section-4">
        <div className="container">
          <div className="section-4__content">
            <h2 className="section-4__title">{t('featuresSection.title')}</h2>
            <div className="section-4__card">
              <div className="section-4__row">
                <div className="section-4__col">
                  <div className="section-4__count">1</div>
                  <img src={spending} alt="v" className="section-4__logo" />
                  <h3 className="section-4__subtitle">
                    {t('featuresSection.spending')}
                  </h3>
                </div>
                <div className="section-4__col">
                  <div className="section-4__num">
                    {data.features.spending.accounts}
                  </div>
                </div>
                <div className="section-4__col">
                  {data.features.spending.flowCompatible ? (
                    <div className="section-4__status section-4__status--success">
                      {t('featuresSection.status.compatible')}
                    </div>
                  ) : (
                    <div className="section-4__status section-4__status--warning">
                      {t('featuresSection.status.incompatible')}
                    </div>
                  )}
                </div>
              </div>
              <div className="section-4__row">
                <div className="section-4__col">
                  <div className="section-4__count">2</div>
                  <img src={savings} alt="v" className="section-4__logo" />
                  <h3 className="section-4__subtitle">
                    {t('featuresSection.saving')}
                  </h3>
                </div>
                <div className="section-4__col">
                  <img src={data.logo} alt="bunq" className="section-4__img" />
                  <div className="section-4__num">
                    {data.features.saving.accounts}
                  </div>
                </div>
                <div className="section-4__col">
                  <img src={flow} alt="flow" className="section-4__img" />
                  {data.features.saving.flowCompatible ? (
                    <div className="section-4__status section-4__status--success">
                      {t('featuresSection.status.compatible')}
                    </div>
                  ) : (
                    <div className="section-4__status section-4__status--warning">
                      {t('featuresSection.status.incompatible')}
                    </div>
                  )}
                </div>
              </div>
              {/* <div className="section-4__row">
                <div className="section-4__col">
                  <div className="section-4__count">3</div>
                  <img src={investing} alt="v" className="section-4__logo" />
                  <h3 className="section-4__subtitle">
                    {t('featuresSection.investing')}
                  </h3>
                </div>
                <div className="section-4__col">
                  {data.features.investing.hasInvesting ? (
                    <img src={success} alt="1" className="section-4__icon" />
                  ) : (
                    <img src={warning} alt="0" className="section-4__icon" />
                  )}
                </div>
                <div className="section-4__col">
                  {data.features.investing.flowCompatible ? (
                    <div className="section-4__status section-4__status--success">
                      {t('featuresSection.status.compatible')}
                    </div>
                  ) : (
                    <div className="section-4__status section-4__status--warning">
                      {t('featuresSection.status.incompatible')}
                    </div>
                  )}
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </section>
      <section className="section-5">
        <div className="container">
          <div className="section-5__card">
            <div className="section-5__info">
              <h3 className="section-5__title">
                {t('bannerSection.title', { name: data.name })}
              </h3>
              <Link
                to={data.link}
                language={i18n.language}
                className="btn btn--primary btn--sm"
              >
                {t('bannerSection.buttonTitle')}
              </Link>
            </div>
            <StaticImage
              src={'../../assets/images/banks/details/bunq-banner.png'}
              alt="banner"
              className="section-5__img"
            />
          </div>
        </div>
      </section>
      <section className="section-6">
        <div className="container">
          <div className="section-6__content">
            <h3 className="section-6__title">{t('opinionSection.title')}</h3>
            <p className="section-6__description">
              {t('opinionSection.quote')}
            </p>
            <div className="founders">
              <div className="founders__wrapper">
                <img
                  src={niels}
                  alt="niels"
                  width="64px"
                  className="founders__img"
                />
                <img
                  src={daan}
                  alt="daan"
                  width="64px"
                  className="founders__img"
                />
              </div>
              <h4 className="founders__title">
                Niels Mulder & Daan van Klinken
              </h4>
              <h5 className="founders__subtitle">
                {t('opinionSection.signature')}
              </h5>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default BankTemplate;
